import React, { useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FeedbackContext } from '@cvt/contexts';


type StoryTabProps = {
  story: Stories.Story;
  onApplyElement?: (element: string, value: string) => void;
};

export const StoryTab: React.FC<StoryTabProps> = ({
  story,
  onApplyElement,
}) => {
  const { triggerFeedback } = React.useContext(FeedbackContext);

  const analysisData = story.structuralFeedback;

  const sectionContent = useMemo(() => {
    if (!analysisData) return '';
    const body = analysisData.narrative
      .map((item:any) => `${item.element}\n— ${item.description}`)
      .join('\n\n');

    return `
Story Analysis - ${story.title}

${body}
    `
      .trim();
  }, [analysisData, story.title]);

  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      triggerFeedback({ message: `${label} copied to clipboard.`, severity: 'success' });
    } catch {
      triggerFeedback({ message: `Unable to copy ${label}.`, severity: 'error' });
    }
  }, [triggerFeedback]);

  const handleCopySection = useCallback(() => {
    copyToClipboard(sectionContent, 'Story Analysis');
  }, [copyToClipboard, sectionContent]);

  if (!analysisData) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary">
              No structural feedback available yet.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Stack spacing={3}>

        <Card variant="outlined">
          <CardHeader
            title={<Typography variant="subtitle1">Story Structure</Typography>}
            action={
              <Button variant="outlined" size="small" onClick={handleCopySection} startIcon={<ContentCopyIcon fontSize="small"/>}>
                    Copy Section
              </Button>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              {analysisData.narrative.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                      No narrative elements available.
                </Typography>
              ) : (
                analysisData.narrative.map((item:any) => (
                  <Box
                    key={item.element}
                    sx={(theme) => ({
                      borderLeft: '2px solid',
                      borderColor: alpha(theme.palette.primary.main, 0.2),
                      pl: 2,
                    })}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {item.element}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            — {item.description}
                    </Typography>
                  </Box>
                ))
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
};
